import { FunctionComponent, useEffect, useMemo, useState } from 'react'

import { useIsFocused, useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { Icons } from 'atoms'
import { useGetScreensTermsQuery, useUpdateScreenTermsMutation } from 'integration/resources/user'
import { Box, Button, Heading, HStack, Modal, useDisclose } from 'native-base'
import { RootStackParamList } from 'navigation/types'

type TermsAcceptanceModalProps = {
  screenName: string
}

export const TermsAcceptanceModal: FunctionComponent<TermsAcceptanceModalProps> = ({
  screenName
}) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()

  const { onOpen, isOpen, onClose } = useDisclose()

  const { data: screensTerms, isLoading } = useGetScreensTermsQuery()

  const updateTermsMutation = useUpdateScreenTermsMutation()

  const isFocused = useIsFocused()

  const screenTerms = useMemo(() => {
    return screensTerms?.data?.data?.find((screen) => screen.screen_name === screenName)
  }, [screensTerms?.data?.data, screenName])

  const [pendingTerms, setPendingTerms] = useState<
    { id: string; terms_title: string; terms_description: string; is_accepted: boolean }[]
  >([])

  useEffect(() => {
    if (!isFocused) return

    if (!isLoading && screenTerms?.terms) {
      setPendingTerms(screenTerms.terms.filter((term) => !term.is_accepted))
    }
  }, [isFocused, isLoading, screenTerms?.terms, screenTerms?.id])

  const currentTerm = pendingTerms[0]

  const termsContent = currentTerm?.terms_description

  const termsTitle = currentTerm?.terms_title ?? 'Termos'

  useEffect(() => {
    if (!isFocused) return

    if (!isLoading && screenTerms) {
      if (!currentTerm && isOpen) {
        onClose()

        return
      }

      if (currentTerm && !isOpen) {
        onOpen()
      }
    }
  }, [currentTerm, isFocused, isLoading, isOpen, onClose, onOpen, screenTerms])

  const handleChooseScreenTerms = (accept: boolean) => {
    onClose()

    if (screenTerms?.id && currentTerm?.id) {
      updateTermsMutation.mutate({
        screen_id: screenTerms.id,
        terms_id: currentTerm.id,
        accept
      })

      if (!accept) navigation.navigate('Home', {})
    }

    setPendingTerms((prevTerms) => prevTerms.filter((term) => term.id !== currentTerm?.id))
  }

  const handleDownloadTerms = () => {
    if (typeof window === 'undefined') return

    if (!termsContent) return

    const blob = new Blob([termsContent], { type: 'text/plain;charset=utf-8' })

    const url = window.URL.createObjectURL(blob)

    const element = document.createElement('a')

    element.setAttribute('href', url)

    element.setAttribute('download', `termos-${screenName}.txt`)

    element.style.display = 'none'

    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)

    window.URL.revokeObjectURL(url)
  }

  if (!screenTerms) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} bgColor="black">
      <Modal.Content minW="642px" borderRadius="20px" bgColor="white" maxW="90%">
        <Modal.Header pt="64px" w="full" pb={6} bg="white" borderBottomWidth={0}>
          <HStack w="full" alignItems="center">
            <Box flex={1}>
              <Heading fontWeight={700} fontSize={24} color="black" textAlign="center">
                {termsTitle}
              </Heading>
            </Box>
          </HStack>

          <Box position="absolute" right={4} top="54px" alignItems="flex-end" zIndex={200}>
            <Button
              variant="ghost"
              colorScheme="gray"
              width="auto"
              onPress={handleDownloadTerms}
              startIcon={<Icons.Downloading size={4} color="gray.600" />}
              zIndex={200}>
              Download dos termos
            </Button>
          </Box>
        </Modal.Header>
        <Modal.Body w="full" h="full" justifyContent="center" alignItems="center" px={6}>
          {termsContent ? (
            <div
              className="terms-details-container"
              style={{
                width: '100%',
                height: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: termsContent }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer
          borderTopWidth={0}
          bgColor="white"
          w="full"
          justifyContent="flex-start"
          pt={6}
          pb="64px"
          px={8}>
          <HStack space={6} w="full">
            <Button
              onPress={() => handleChooseScreenTerms(false)}
              isDisabled={updateTermsMutation.isLoading}
              isLoading={updateTermsMutation.isLoading}
              borderColor="gray.700"
              variant="outline"
              flex={1}
              _text={{
                color: 'gray.700',
                fontSize: '14px',
                lineHeight: '16px'
              }}>
              Recusar
            </Button>
            <Button
              isDisabled={updateTermsMutation.isLoading}
              isLoading={updateTermsMutation.isLoading}
              colorScheme="gray"
              flex={1}
              onPress={() => handleChooseScreenTerms(true)}>
              Aceitar Termos
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
